import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { UserManager, User, Log, UserManagerSettings } from 'oidc-client';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { from } from 'rxjs';
import { TokenService } from './token.service';

export function getClientSettings(): UserManagerSettings {
  return {
    authority: environment.IDSAuthority,
    client_id: environment.IDSClientID,
    redirect_uri: environment.redirect_uri,
    acr_values: environment.IDSArcValue,
    post_logout_redirect_uri: environment.post_logout_redirect_uri,
    response_type: environment.response_type,
    scope: environment.IDSScope,
    accessTokenExpiringNotificationTime: 4,
    filterProtocolClaims: true,
    clockSkew: 5,
    loadUserInfo: true,
    // --- Firefox / Safari fix (normal, non-private windows) ---
    // Both browsers (Firefox Total Cookie Protection, Safari ITP) block a cross-site
    // iframe from accessing the identity provider's partitioned session cookie.
    //
    // monitorSession:false disables oidc-client's "check session" feature, which
    // otherwise loads login.iotconnect.io/connect/checksession in a hidden cross-site
    // iframe and polls it. In Firefox/Safari that iframe can't read the IdP cookie, so
    // the check reports the session as changed/invalid and fires addUserSignedOut(),
    // which triggers our logout + redirect -> the login/logout loop. The access token's
    // own expiry (addAccessTokenExpired) still governs re-authentication, so no security
    // is lost by not polling the IdP iframe.
    monitorSession: false,
    // automaticSilentRenew defaults to true in oidc-client. With Implicit Flow and no
    // silent_redirect_uri, silent renew also uses a hidden cross-site iframe to the IdP,
    // which fails the same way in Firefox/Safari. Disable it so renewal happens via a
    // normal top-level redirect when the token actually expires, instead of a failing
    // background iframe that contributes to the loop.
    automaticSilentRenew: false
  };
}

Log.logger = console;
Log.level = Log.DEBUG;

@Injectable({
  providedIn: 'root'
})

export class IDSAuthService {
  private mgr = new UserManager(getClientSettings());
  private user: User | null = null;
  browserCallBackURL: string = '';
  userDetails: any;
  private tokenExpiredTimeoutId: any = null;
  private refreshInProgress = false;

  constructor(
    private _toasterService: ToastrService,
    private tokenService: TokenService
  ) {

    this.mgr.getUser().then(user => {
      this.user = user;
    });

    this.mgr.events.addUserSignedOut(() => {
      this.clearTokenRefreshState();
      localStorage.clear();
      window.location.href = environment.post_logout_redirect_uri;
    });

    this.mgr.events.addUserLoaded((data) => {
      if (data['state'] != undefined && data['state'] != null && data['state'] != '') {
        this.browserCallBackURL = data['state'];
      }
      // Clear refresh state when a new user is loaded (successful re-auth)
      this.refreshInProgress = false;
      this.clearTokenExpiredTimeout();
    });

    this.mgr.events.addAccessTokenExpired(data => {
      // Don't handle expiry if we're already refreshing
      if (this.refreshInProgress) return;

      this.refreshInProgress = true;
      this.tokenExpiredTimeoutId = setTimeout(() => {
        // A fresh token may have been obtained since this event fired
        // (e.g. the /callback route completed a silent re-auth while this timer was pending).
        // Only re-authenticate if the token is still expired; otherwise skip to avoid
        // disrupting the newly obtained session.
        if (!this.tokenService.isTokenValid() && this.refreshInProgress) {
          localStorage.clear();
          this.endSignoutMainWindow(true);
          this._toasterService.success("Session Timeout", "Error");
        }
      }, 5000)
    })

  }

  private clearTokenExpiredTimeout(): void {
    if (this.tokenExpiredTimeoutId !== null) {
      clearTimeout(this.tokenExpiredTimeoutId);
      this.tokenExpiredTimeoutId = null;
    }
  }

  private clearTokenRefreshState(): void {
    this.refreshInProgress = false;
    this.clearTokenExpiredTimeout();
  }

  markTokenRefreshComplete(): void {
    this.clearTokenRefreshState();
  }

  isAuthenticated(): boolean {
    return this.tokenService.hasToken() && this.tokenService.isTokenValid();
  }

  getClaims(): any {
    return this.user ? this.user.profile : null;
  }


  getLogedInUserIDSData(): Observable<User | null> {
    return from(this.mgr.getUser()).pipe(map<User | null, User | null>((user) => {
      if (user) {
        return user;
      } else {
        return null;
      }
    })
    )
  }

  isLoggedInObs(): Observable<boolean> {

    return from(this.mgr.getUser()).pipe(map<User | null, boolean>((user) => {
      this.user = user;
      if (user) {
        return true;
      } else {
        return false;
      }
    })
    )
  }

  endSigninMainWindow(): Observable<User | null> {
    return from(this.mgr.signinRedirectCallback()).pipe(
      map((user: User | null) => {
        if (user) {
          return user; // Return the user if not null
        } else {
          return null; // Explicitly return null if the user is null
        }
      }),
      catchError(err => {
        console.error(err?.message?.toLowerCase());
        window.location.href = environment.post_logout_redirect_uri;
        throw new Error('Error in source. Details: ' + err);
      })
    );
  }

  async endSignoutMainWindow(_isReloadPage: boolean = false): Promise<void> {
    if (_isReloadPage) {
      return this.mgr.signinRedirect();
    } else {
      try {
        localStorage.clear();
        return this.mgr.signoutRedirect().catch((_err) => { });
      } catch (_err) { }
    }
  }

  getAuthorizationHeaderValue(): string {
    return `${this.user ? this.user.token_type : null} ${this.user ? this.user.access_token : null}`;
  }

  startSigninMainWindow(url: string = ''): Promise<void> {
    this.browserCallBackURL = url;
    return this.mgr.signinRedirect({ data: url, login_hint: localStorage.getItem("companyUserEmail") });
  }

  completeAuthentication(): Promise<void> {
    return this.mgr.signinRedirectCallback().then(user => {
      this.user = user;
    });
  }

  login(body: any) {
    return this.mgr.signinRedirect();
  }

  isLoggedIn(): boolean {
    return this.user != null && !this.user.expired;
  }
}
